<template>
  <v-card>
    <v-card-item>
      <v-card-title>
        {{ team.name }}
      </v-card-title>
    </v-card-item>
    <v-card-text>
      <v-row>
        <v-col cols="2">
          <v-sheet rounded="lg">
            <v-list-item
              v-for="(item, index) in listItems"
              :key="index"
              :class="{ active: currentTab === item.name }"
              @click="selectTab(item.name)">
              {{ item.label }}
              <v-badge
                v-if="item.name == 'notes' && teamNoteCount > 0"
                color="error"
                :content="teamNoteCount"
                inline></v-badge>
            </v-list-item>
          </v-sheet>
        </v-col>

        <v-col>
          <v-sheet min-height="70vh" rounded="lg">
            <component
              :is="currentComponent"
              v-bind="componentProps"
              @snack="throwSnack"></component>
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import InfoSubView from '@/views/admin/teams/subviews/InfoSubView.vue';
import RegistrationSubView from '@/views/admin/teams/subviews/RegistrationSubView.vue';
import NotesSubView from '@/views/admin/teams/subviews/NotesSubView.vue';
import { useRoute, useRouter } from 'vue-router';
import { ITeamDTO, TeamDTO } from '@/dtos';
import { useNoteStore } from '@/stores/modules/admin';
import { SNACKSTATUS } from '@/stores/util';
import { useSnack } from '@/helpers';
import { useTeamStore } from '@/stores';
import EntryFormSubView from './subviews/EntryFormSubView.vue';
import CastCrewSubView from './subviews/CastCrewSubView.vue';
import ReleasesSubView from './subviews/ReleasesSubView.vue';
import SubmissionMediaSubView from './subviews/SubmissionMediaSubView.vue';
import CriteriaSubView from './subviews/CriteriaSubView.vue';
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);
const route = useRoute();
const router = useRouter();
const teamId = ref(
  Array.isArray(route.params.teamId) ? route.params.teamId[0] : (route.params.teamId ?? ''),
);
const yearId = ref(
  Array.isArray(route.params.yearId) ? route.params.yearId[0] : (route.params.yearId ?? ''),
);
const noteStore = useNoteStore();
const teamStore = useTeamStore();
const { data } = teamStore.getTeamForBranchYear(teamId, yearId);
const team = ref<ITeamDTO>(new TeamDTO());
watch(data, (value) => {
  if (value) {
    team.value = value;
  }
});

const teamNoteCount = computed(() => {
  return noteStore.notesQuery.data?.length ?? 0;
});
const props = ref({ yearId: yearId, teamId: teamId, team: team });
const teamCriteriaId = computed(() => team.value.teamCriteria[0]?.id ?? '');

watch(
  () => team.value,
  () => {
    noteStore.teamCriteriaId = teamCriteriaId.value;
  },
);

const listItems = [
  { name: 'info', label: 'Info', component: InfoSubView, props: props.value },
  {
    name: 'registration',
    label: 'Registration',
    component: RegistrationSubView,
    props: props.value,
  },
  {
    name: 'criteria-selected',
    label: 'Selected Criteria',
    component: CriteriaSubView,
    props: props.value,
  },
  {
    name: 'submission',
    label: 'Submission & Videos',
    component: SubmissionMediaSubView,
    props: props.value,
  },
  {
    name: 'entryForm',
    label: 'Entry Form',
    component: EntryFormSubView,
    props: props.value,
  },
  {
    name: 'castAndCrew',
    label: 'Cast & Crew',
    component: CastCrewSubView,
    props: props.value,
  },
  {
    name: 'releases',
    label: 'Releases',
    component: ReleasesSubView,
    props: props.value,
  },
  { name: 'notes', label: 'Notes', component: NotesSubView },
];

const currentTab = ref(route.params.tabName || 'info');

// Compute the current component to be displayed
let currentComponent = computed(() => {
  const item = listItems.find((i) => i.name === currentTab.value);
  return item ? item.component : null;
});

let componentProps = computed(() => {
  const item = listItems.find((i) => i.name === currentTab.value);
  return item ? item.props : null;
});

// Function to change the selected tab
function selectTab(tabName: string) {
  currentTab.value = tabName;
}

watch(
  currentTab,
  (newTab) => {
    router.push({
      name: 'AdminYearTeamDetailView',
      params: { yearId: yearId.value, teamId: teamId.value, tabName: newTab },
    });
  },
  { immediate: true },
);
</script>
